import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductLibrarayService } from '../../services/product-libraray.service';
import { InventoryItemService } from '../../services/inventory-item.service';
import { CommonModule } from '@angular/common';
import { CreateProductDTO, DigitalSpecsDTO, FlexoSpecsDTO, MaterialDTO, OffsetSpecsDTO, ProductionDataDTO } from '../../interface/product-library.entity';
import { FormsModule } from '@angular/forms';
import { NotificationService, DotLoadingServices } from '../../../../core';

@Component({
  selector: 'app-production-data-add',
  imports: [CommonModule, FormsModule],
  templateUrl: './production-data-add.html',
  styleUrl: './production-data-add.scss'
})
export class ProductionDataAdd {

  protected router = inject(Router);
  protected route = inject(ActivatedRoute);
  protected productLibraryService = inject(ProductLibrarayService);
  private inventoryItemService = inject(InventoryItemService);
  private notification = inject(NotificationService);
  private loading = inject(DotLoadingServices);

  productName: string = '';
  noOfColors: number | null = null;
  noOfUps: number | null = null;
  printingMachine: string = '';
  selectedMaterial: string = '';
  isEdit = false;

  selectedDepartment: string = '';
  category: 'Cartoon' | 'Gift Box' | 'Book' | 'Label' | '' = '';
  isCategoryEnabled: boolean = false;
  customer: string = '';
  productCode: string = '';
  customerProductCode: string = '';
  salesOrder: string = '';
  quotationNo: string = '';
  artworkNo: string = '';
  version: string = '';
  proofStandard: string = '';
  windingDirection: string = '';
  coreSize: string = '';
  flexoType: string = ''; // 'Reel' | 'Sheet'
  pcsPerRoll: number | null = null;
  flexoRemark: string = '';
  widthMm: string | number | null = null;
  lengthMm: string | number | null = null;
  tolerance: number | null = null;
  cylinderTeeth: number | null = null;
  cutSheetSize: number | null = null;
  fullSheetSize: number | null = null;
  productSize: number | null = null;
  selectedPastingType: string = '';
  selectedGiftBoxBoardType: string = '';
  thickness: number | null = null;
  selectedGiftBoxType: string = '';
  selectedBookCoverType: string = '';
  coverThickness: number | null = null;
  coverLength: number | null = null;
  coverWidth: number | null = null;
  innerpageLength: number | null = null;
  innerpageWidth: number | null = null;
  selectedPageBindingType: string = '';
  innerPageLength: number | null = null;

  machines = signal<any[]>([]);
  printingMachineDropdownOpen: boolean = false;

  // ── Materials (inventory search, multi-row) ──────────────────────────────
  materials = signal<MaterialDTO[]>([
    { sequence: 1, material: '', materialCode: '' }
  ]);
  materialSearchTerms: string[] = [''];
  materialDropdownOpen: number | null = null;
  materialInventoryResults = signal<any[]>([]);
  materialLoading: boolean = false;
  private materialDebounce: any;

  // ── Ink (inventory search) ────────────────────────────────────────────────
  inkSearchTerms: string[] = [''];
  inkDropdownOpen: number | null = null;
  inkInventoryResults = signal<any[]>([]);
  inkLoading: boolean = false;
  private inkDebounce: any;

  inks = signal<{ sequence: number; ink: string }[]>([
    { sequence: 1, ink: '' }
  ]);

  varnish = signal<{ sequence: number; varnish: string }[]>([
    { sequence: 1, varnish: '' }
  ]);

  screens = signal<{ sequence: number; screen: string }[]>([
    { sequence: 1, screen: '' }
  ]);

  finishings = signal<{ stepId: string; sequence: number; process: string; machine: string; remarks: string }[]>([
    { stepId: 'f1', sequence: 1, process: '', machine: '', remarks: '' }
  ]);

  // ── Finishings (process/machine searchable dropdowns) ─────────────────────
  finishingsList = signal<any[]>([]);
  machineSearch: string[] = [''];
  finishingSearch: string[] = [''];
  machineDropdownOpen: number | null = null;
  finishingDropdownOpen: number | null = null;

  coldFoil: string = '';
  hotFoil: string = '';
  embossing: string = '';
  lamination: string = '';
  dieCutCode: string = '';

  ngOnInit() {

    this.productLibraryService.getMachines().subscribe({
      next: (res) => this.machines.set(res.data?.machines ?? []),
      error: (err) => console.error('Failed to load machines', err)
    });

    this.productLibraryService.getFinishings().subscribe({
      next: (res) => this.finishingsList.set(res.data ?? []),
      error: (err) => console.error('Failed to load finishings', err)
    });

    this.route.queryParams.subscribe(params => {
      const code = params['code'];
      const mode = params['mode'];

      if (code) {
        this.loadProductData(code);
      }

      if (code && mode === 'edit') {
        this.isEdit = true;
        this.loadProductData(code);
      }
    });
  }

  filteredPrintingMachines() {
    const search = (this.printingMachine || '').toLowerCase();
    return this.machines().filter(m =>
        m.capabilities?.toLowerCase().includes('printing') &&
        m.machineName.toLowerCase().includes(search)
    );
  }

  selectPrintingMachine(machineName: string) {
    this.printingMachine = machineName;
    this.printingMachineDropdownOpen = false;
  }

  onPrintingMachineBlur() {
    setTimeout(() => {
      this.printingMachineDropdownOpen = false;
    }, 200);
  }

  // ── Finishings (process/machine searchable dropdowns) ─────────────────────

  filteredMachines(index: number, capability?: string) {
    const search = (this.machineSearch[index] || '').toLowerCase();
    return this.machines().filter(m =>
        (!capability || m.capabilities?.toLowerCase().includes(capability.toLowerCase())) &&
        m.machineName.toLowerCase().includes(search)
    );
  }

  filteredFinishings(index: number) {
    const search = (this.finishingSearch[index] || '').toLowerCase();
    return this.finishingsList().filter(f =>
        f.Finishing.toLowerCase().includes(search)
    );
  }

  selectMachine(index: number, machineName: string) {
    this.finishings.update(list =>
        list.map((item, i) => i === index ? { ...item, machine: machineName } : item)
    );
    this.machineSearch[index] = machineName;
    this.machineDropdownOpen = null;
  }

  selectFinishing(index: number, finishingName: string) {
    this.finishings.update(list =>
        list.map((item, i) => i === index ? { ...item, process: finishingName } : item)
    );
    this.finishingSearch[index] = finishingName;
    this.finishingDropdownOpen = null;
  }

  onFinishingBlur(index: number) {
    setTimeout(() => {
      this.finishingDropdownOpen = null;
      const current = this.finishings()[index];
      if (!this.finishingSearch[index]) {
        this.finishingSearch[index] = current.process;
      }
    }, 200);
  }

  onMachineBlur(index: number) {
    setTimeout(() => {
      this.machineDropdownOpen = null;
      const current = this.finishings()[index];
      if (!this.machineSearch[index]) {
        this.machineSearch[index] = current.machine;
      }
    }, 200);
  }

  addFinishing() {
    const nextIndex = this.finishings().length + 1;
    this.finishingSearch[nextIndex - 1] = '';
    this.machineSearch[nextIndex - 1] = '';
    this.finishings.update(list => [
      ...list,
      { stepId: `f${nextIndex}`, sequence: nextIndex, process: '', machine: '', remarks: '' }
    ]);
  }

  removeFinishing(index: number) {
    this.finishings.update(list => {
      if (list.length <= 1) return list;
      const updated = list.filter((_, i) => i !== index);
      return updated.map((item, i) => ({ ...item, stepId: `f${i + 1}`, sequence: i + 1 }));
    });
    this.finishingSearch.splice(index, 1);
    this.machineSearch.splice(index, 1);
  }


  // ── Material search (inventory, multi-row) ───────────────────────────────

  onMaterialInput(index: number) {
    this.materialDropdownOpen = index;
    clearTimeout(this.materialDebounce);
    this.materialDebounce = setTimeout(() => {
      this.fetchMaterialItems(this.materialSearchTerms[index] ?? '');
    }, 300);
  }

  fetchMaterialItems(search: string) {
    this.materialLoading = true;
    this.inventoryItemService.getInventoryItems({
      itemCode: search,
      itemsPerPage: 20,
      pageNumber: 1
    }).subscribe({
      next: (res: any) => {
        const items = res?.data?.data ?? res?.data ?? [];
        this.materialInventoryResults.set(items);
        this.materialLoading = false;
      },
      error: (err) => {
        console.error('Failed to load inventory items', err);
        this.materialInventoryResults.set([]);
        this.materialLoading = false;
      }
    });
  }

  selectMaterial(index: number, item: any) {
    const display = `${item.inventoryItemCode} – ${item.inventoryItemDescription}`;
    this.materials.update(list =>
      list.map((m, i) =>
        i === index
          ? { ...m, material: item.inventoryItemDescription, materialCode: item.inventoryItemCode }
          : m
      )
    );
    this.materialSearchTerms[index] = display;
    this.materialDropdownOpen = null;
  }

  onMaterialBlur(index: number) {
    setTimeout(() => { this.materialDropdownOpen = null; }, 200);
  }

  addMaterial() {
    const nextIndex = this.materials().length + 1;
    this.materialSearchTerms[nextIndex - 1] = '';
    this.materials.update(list => [...list, { sequence: nextIndex, material: '', materialCode: '' }]);
  }

  removeMaterial(index: number) {
    this.materials.update(list => {
      if (list.length <= 1) return list;
      const updated = list.filter((_, i) => i !== index);
      return updated.map((item, i) => ({ ...item, sequence: i + 1 }));
    });
    this.materialSearchTerms.splice(index, 1);
  }

  fetchInkItems(search: string) {
    this.inkLoading = true;
    this.inventoryItemService.getInventoryItems({
      itemCode: search,
      itemsPerPage: 20,
      itemType : 2,
      pageNumber: 1
    }).subscribe({
      next: (res: any) => {
        const items = res?.data?.data ?? res?.data ?? [];
        this.inkInventoryResults.set(items);
        this.inkLoading = false;
      },
      error: (err) => {
        console.error('Failed to load ink items', err);
        this.inkInventoryResults.set([]);
        this.inkLoading = false;
      }
    });
  }
  // ── Ink search (inventory gRPC via InventoryItemService, filter by ink) ──

  onInkInput(index: number) {
    this.inkDropdownOpen = index;
    clearTimeout(this.inkDebounce);
    this.inkDebounce = setTimeout(() => {
      this.fetchInkItems(this.inkSearchTerms[index] ?? '');
    }, 300);
  }


  selectInk(index: number, item: any) {
    const display = `${item.inventoryItemCode} – ${item.inventoryItemDescription}`;
    this.inks.update(list =>
        list.map((ink, i) =>
            i === index ? { ...ink, ink: display } : ink
        )
    );
    this.inkSearchTerms[index] = display;
    this.inkDropdownOpen = null;
  }

  onInkBlur(index: number) {
    setTimeout(() => {
      this.inkDropdownOpen = null;
    }, 200);
  }

  // ─────────────────────────────────────────────────────────────────────────

  private loadProductData(code: string) {
    this.loading.set(true);
    this.productLibraryService.getProductById(code).subscribe({
      next: (res) => {
        const product = res.data;

        // --- General Data ---
        this.productCode = product.productCode;
        this.productName = product.generalData?.productName ?? '';
        this.customer = product.generalData?.customer ?? '';
        this.customerProductCode = product.generalData?.customerProductCode ?? '';
        this.salesOrder = product.generalData?.references?.[0]?.salesOrder ?? '';
        this.quotationNo = product.generalData?.references?.[0]?.quotationNo ?? '';
        this.artworkNo = product.generalData?.artwork?.[0]?.artworkNo ?? '';
        this.version = product.generalData?.artwork?.[0]?.version ?? '';

        // --- Department & Category ---
        this.selectedDepartment = product.department ?? '';
        this.category = product.generalData?.category ?? '';

        // --- Finishings ---
        const mappedFinishings = product.finishings?.map(f => ({
          stepId: f.stepId ?? '',
          sequence: f.sequence ?? 0,
          process: f.process ?? '',
          machine: f.machine ?? '',
          remarks: f.remarks ?? ''
        })) ?? [{ stepId: 'f1', sequence: 1, process: '', machine: '', remarks: '' }];

        this.finishings.set(mappedFinishings);

        // Pre-populate search arrays so inputs show existing values immediately
        this.finishingSearch = mappedFinishings.map(f => f.process);
        this.machineSearch = mappedFinishings.map(f => f.machine);

        // --- Technical Data ---
        const td = product.technicalData as OffsetSpecsDTO | FlexoSpecsDTO | DigitalSpecsDTO | undefined;

        if (td) {
          if ('fullSheetSize' in td) {
            this.fullSheetSize = td.fullSheetSize ?? 0;
            this.cutSheetSize = td.cutSheetSize ?? 0;
            this.productSize = td.productSize ?? 0;
            this.selectedPastingType = (td as OffsetSpecsDTO).pastingType ?? '';
            this.widthMm = (td as OffsetSpecsDTO).widthMm ?? null;
            this.lengthMm = (td as OffsetSpecsDTO).lengthMm ?? null;

            if ((td as OffsetSpecsDTO).giftBox) {
              this.selectedGiftBoxBoardType = (td as OffsetSpecsDTO).giftBox?.boardType ?? '';
              this.thickness = (td as OffsetSpecsDTO).giftBox?.thickness ?? 0;
              this.selectedGiftBoxType = (td as OffsetSpecsDTO).giftBox?.boxType ?? '';
            }

            if ((td as OffsetSpecsDTO).book) {
              const book = (td as OffsetSpecsDTO).book!;
              this.selectedBookCoverType = book.coverType ?? '';
              this.coverThickness = book.coverThickness ?? 0;
              this.coverWidth = book.coverWidth ?? 0;
              this.coverLength = book.coverLength ?? 0;
              this.innerpageWidth = book.innerPageWidth ?? 0;
              this.innerpageLength = book.innerPageLength ?? 0;
              this.selectedPageBindingType = book.bindingType ?? '';
            }

            const printData = (td as OffsetSpecsDTO).print?.[0];
            if (printData) {
              this.noOfColors = printData.noOfColors ?? 0;
              this.noOfUps = printData.noOfUps ?? 0;
              this.proofStandard = printData.proofStandard ?? '';
              this.printingMachine = printData.printingMachine ?? '';
            }
          }

          if ('flexoType' in td) {
            this.flexoType = (td as FlexoSpecsDTO).flexoType ?? '';
            this.coreSize = (td as FlexoSpecsDTO).coreSize ?? '';
            this.pcsPerRoll = (td as FlexoSpecsDTO).pcsPerRoll ?? null;
            this.windingDirection = (td as FlexoSpecsDTO).windingDirection ?? '';
            this.flexoRemark = (td as FlexoSpecsDTO).remark ?? '';
            this.widthMm = (td as FlexoSpecsDTO).widthMm ?? null;
            this.lengthMm = (td as FlexoSpecsDTO).lengthMm ?? null;
            this.tolerance = (td as FlexoSpecsDTO).tolerance ?? null;
            this.cylinderTeeth = (td as FlexoSpecsDTO).cylinderTeeth ?? null;

            const printData = (td as FlexoSpecsDTO).print?.[0];
            if (printData) {
              this.noOfColors = printData.noOfColors ?? 0;
              this.noOfUps = printData.noOfUps ?? 0;
              this.proofStandard = printData.proofStandard ?? '';
              this.printingMachine = printData.printingMachine ?? '';
            }
          }

          // Digital — print data only
          if (!('fullSheetSize' in td) && !('flexoType' in td) && 'print' in td) {
            const printData = (td as DigitalSpecsDTO).print?.[0];
            if (printData) {
              this.noOfColors = printData.noOfColors ?? 0;
              this.noOfUps = printData.noOfUps ?? 0;
              this.proofStandard = printData.proofStandard ?? '';
              this.printingMachine = printData.printingMachine ?? '';
            }
          }
        }

        const pd = product.productionData as ProductionDataDTO;

        if (pd) {
          // Handle both the new materials array and the legacy selectedMaterial string
          const pdAny = pd as any;
          if (pd.materials?.length) {
            const materialList = pd.materials.map(m => ({
              sequence: m.sequence ?? 0,
              material: m.material ?? '',
              materialCode: m.materialCode ?? ''
            }));
            this.materials.set(materialList);
            this.materialSearchTerms = materialList.map(m =>
              m.materialCode ? `${m.materialCode} – ${m.material}` : m.material
            );
          } else if (pdAny.selectedMaterial) {
            // Backward-compat: upgrade legacy single string to one row
            this.materials.set([{ sequence: 1, material: pdAny.selectedMaterial, materialCode: '' }]);
            this.materialSearchTerms = [pdAny.selectedMaterial];
          } else {
            this.materials.set([{ sequence: 1, material: '', materialCode: '' }]);
            this.materialSearchTerms = [''];
          }

          // FIX 1: Handle empty inks array — fall back to default
          const inkList = pd.inks?.length
              ? pd.inks.map(f => ({ sequence: f.sequence ?? 0, ink: f.ink ?? '' }))
              : [{ sequence: 1, ink: '' }];
          this.inks.set(inkList);
          this.inkSearchTerms = this.inks().map(i => i.ink);

          // FIX 2: Handle empty varnishes array — fall back to default
          const varnishList = pd.varnishes?.length
              ? pd.varnishes.map(f => ({ sequence: f.sequence ?? 0, varnish: f.varnish ?? '' }))
              : [{ sequence: 1, varnish: '' }];
          this.varnish.set(varnishList);

          // FIX 3: Handle empty screens array — fall back to default
          const screenList = pd.screens?.length
              ? pd.screens.map(f => ({ sequence: f.sequence ?? 0, screen: f.screen ?? '' }))
              : [{ sequence: 1, screen: '' }];
          this.screens.set(screenList);

          this.coldFoil = pd.coldFoil ?? '';
          this.hotFoil = pd.hotFoil ?? '';
          this.embossing = pd.embossing ?? '';
          this.lamination = pd.lamination ?? '';  // FIX 5: was pd.dieCutCode (bug)
          this.dieCutCode = pd.dieCutCode ?? '';
        }
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Failed to load product for edit', err);
        this.loading.set(false);
      }
    });
  }

  goBack() {
    this.router.navigate(['/production-data']);
    this.resetForm();
  }

  addInk() {
    const current = this.inks();
    const nextIndex = current.length + 1;

    const newItem = {
      sequence: nextIndex,
      ink: '',
    };
    this.inkSearchTerms[nextIndex - 1] = '';

    this.inks.update(list => [...list, newItem]);
  }

  removeInk(index: number) {
    this.inks.update(list => {
      if (list.length <= 1) return list;
      const updated = list.filter((_, i) => i !== index);
      return updated.map((item, i) => ({
        ...item,
        sequence: i + 1
      }));
    });
    this.inkSearchTerms.splice(index, 1);
  }

  addVarnish() {
    const current = this.varnish();
    const nextIndex = current.length + 1;

    const newItem = {
      sequence: nextIndex,
      varnish: '',
    };

    this.varnish.update(list => [...list, newItem]);
  }

  removeVarnish(index: number) {
    this.varnish.update(list => {
      if (list.length <= 1) return list;
      const updated = list.filter((_, i) => i !== index);
      return updated.map((item, i) => ({
        ...item,
        sequence: i + 1
      }));
    });
  }

  addScreen() {
    const current = this.screens();
    const nextIndex = current.length + 1;

    const newItem = {
      sequence: nextIndex,
      screen: '',
    };

    this.screens.update(list => [...list, newItem]);
  }

  removeScreen(index: number) {
    this.screens.update(list => {
      if (list.length <= 1) return list;
      const updated = list.filter((_, i) => i !== index);
      return updated.map((item, i) => ({
        ...item,
        sequence: i + 1
      }));
    });
  }

  saveProductionData() {

    if (!this.printingMachine?.trim()) {
      this.notification.set({ type: 'error', message: 'Printing Machine is required.' });
      return;
    }

    const payload: CreateProductDTO = {
      department: this.selectedDepartment as 'Flexo' | 'Offset' | 'Digital',
      generalData: {
        customerProductCode: this.customerProductCode,
        productName: this.productName,
        customer: this.customer,
        category: this.category || undefined,
        references: [
          {
            salesOrder: this.salesOrder,
            quotationNo: this.quotationNo
          }
        ],
        artwork: [
          {
            artworkNo: this.artworkNo,
            version: this.version
          }
        ]
      },
      technicalData: this.buildSpecifications() || undefined,
      finishings: this.finishings(),
      productionData: this.buildOperationData() || undefined
    };

    this.loading.set(true);
    this.productLibraryService.updateProduct(this.productCode, payload).subscribe({
      next: res => {
        console.log('Product updated successfully', res);
        this.notification.set({ type: 'success', message: 'Product updated successfully.' });
        this.resetForm();
        this.loading.set(false);
      },
      error: err => {
        console.error('Failed to update product', err);
        this.notification.set({ type: 'error', message: 'Failed to update product.' });
        this.loading.set(false);
      }
    });
  }

  private buildSpecifications(): OffsetSpecsDTO | FlexoSpecsDTO | DigitalSpecsDTO {
    if (this.selectedDepartment === 'Digital') {
      return {
        print: [
          {
            noOfColors: this.noOfColors!,
            noOfUps: this.noOfUps!,
            proofStandard: this.proofStandard!,
            printingMachine: this.printingMachine!
          }
        ]
      };
    }

    if (this.selectedDepartment === 'Offset') {
      return {
        fullSheetSize: this.fullSheetSize!,
        cutSheetSize: this.cutSheetSize!,
        productSize: this.productSize!,
        pastingType: this.selectedPastingType || undefined,
        widthMm: this.widthMm,
        lengthMm: this.lengthMm,
        giftBox: this.category === 'Gift Box' ? {
          boardType: this.selectedGiftBoxBoardType!,
          thickness: this.thickness!,
          boxType: this.selectedGiftBoxType!
        } : undefined,
        book: this.category === 'Book' ? {
          coverType: this.selectedBookCoverType!,
          coverThickness: this.coverThickness!,
          coverWidth: this.coverWidth!,
          coverLength: this.coverLength!,
          innerPageWidth: this.innerpageWidth!,
          innerPageLength: this.innerPageLength!,
          bindingType: this.selectedPageBindingType!
        } : undefined,
        print: [
          {
            noOfColors: this.noOfColors!,
            noOfUps: this.noOfUps!,
            proofStandard: this.proofStandard!,
            printingMachine: this.printingMachine!
          }
        ]
      };
    }

    if (this.selectedDepartment === 'Flexo') {
      const isReel = this.flexoType === 'Reel';
      return {
        flexoType: this.flexoType,
        coreSize: isReel ? this.coreSize : undefined,
        noOfUps: isReel ? this.noOfUps : undefined,
        pcsPerRoll: isReel ? this.pcsPerRoll : undefined,
        windingDirection: isReel ? this.windingDirection : undefined,
        remark: this.flexoRemark || undefined,
        widthMm: this.widthMm,
        lengthMm: this.lengthMm,
        tolerance: this.tolerance,
        cylinderTeeth: this.cylinderTeeth,
        print: [
          {
            noOfColors: this.noOfColors!,
            noOfUps: this.noOfUps!,
            proofStandard: this.proofStandard!,
            printingMachine: this.printingMachine!
          }
        ]
      };
    }

    return undefined as any;
  }

  private buildOperationData(): ProductionDataDTO {
    return {
      materials: this.materials(),
      inks: this.inks(),
      varnishes: this.varnish(),
      screens: this.screens(),
      coldFoil: this.coldFoil,
      hotFoil: this.hotFoil,
      embossing: this.embossing,
      lamination: this.lamination,
      dieCutCode: this.dieCutCode,
      productionDataAdded: true
    };
  }

  resetForm(): void {
    this.materials.set([{ sequence: 1, material: '', materialCode: '' }]);
    this.materialSearchTerms = [''];
    this.materialDropdownOpen = null;

    this.coldFoil = '';
    this.hotFoil = '';
    this.embossing = '';
    this.lamination = '';
    this.dieCutCode = '';

    this.inks.set([{ sequence: 1, ink: '' }]);
    this.inkSearchTerms = [''];
    this.varnish.set([{ sequence: 1, varnish: '' }]);
    this.screens.set([{ sequence: 1, screen: '' }]);

    this.finishings.set([{ stepId: 'f1', sequence: 1, process: '', machine: '', remarks: '' }]);
    this.finishingSearch = [''];
    this.machineSearch = [''];
    this.finishingDropdownOpen = null;
    this.machineDropdownOpen = null;

    this.widthMm = null;
    this.lengthMm = null;
    this.tolerance = null;
    this.cylinderTeeth = null;
    this.fullSheetSize = null;
    this.cutSheetSize = null;
    this.productSize = null;
  }
}